/**
 * File Utilities
 *
 * Helper functions for file operations, icons, and formatting.
 *
 * @module utils/fileUtils
 */

const VSCODE_ICONS_BASE =
  'https://raw.githubusercontent.com/vscode-icons/vscode-icons/master/icons';

/**
 * Get file icon (CDN URL or emoji fallback)
 * @param {string} fileName - Full filename with extension
 * @returns {string} Icon URL or emoji
 */
export const getFileIcon = fileName => {
  const ext = fileName.split('.').pop()?.toLowerCase();

  const cdnIcons = {
    pdf: `${VSCODE_ICONS_BASE}/file_type_pdf.svg`,
    doc: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
    docx: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
    rtf: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
    xls: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    xlsx: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    csv: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    ppt: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
    pptx: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
  };

  // Return CDN icon if available
  if (cdnIcons[ext]) {
    return cdnIcons[ext];
  }

  // Emoji fallbacks for other file types
  const emojiIcons = {
    txt: '📃',
    md: '📝',
    jpg: '🖼️',
    jpeg: '🖼️',
    png: '🖼️',
    gif: '🖼️',
    webp: '🖼️',
    tiff: '🖼️',
    bmp: '🖼️',
  };

  return emojiIcons[ext] || '📎';
};

/**
 * Get tooltip text for file type
 * @param {string} ext - File extension
 * @returns {string} Tooltip text
 */
export const getFileTypeTooltip = ext => {
  const normalizedExt = (ext || '').toLowerCase().replace(/^\./, '');

  const tooltips = {
    pdf: 'PDF Document - pdf',
    doc: 'Word Document - doc',
    docx: 'Word Document - docx',
    xls: 'Excel Document - xls',
    xlsx: 'Excel Document - xlsx',
    csv: 'Comma Separated Values - csv',
    ppt: 'PowerPoint Presentation - ppt',
    pptx: 'PowerPoint Presentation - pptx',
    jpg: 'JPEG Image - jpg',
    jpeg: 'JPEG Image - jpeg',
    png: 'PNG Image - png',
    gif: 'GIF Image - gif',
    webp: 'WebP Image - webp',
    bmp: 'Bitmap Image - bmp',
    tiff: 'TIFF Image - tiff',
    txt: 'Text File - txt',
    md: 'Markdown File - md',
    rtf: 'Rich Text Format - rtf',
  };

  return tooltips[normalizedExt] || `File - ${normalizedExt || 'unknown'}`;
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string
 */
export const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Check if file extension is an image
 * @param {string} ext - File extension
 * @returns {boolean}
 */
export const isImageExtension = ext => {
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff'].includes(ext?.toLowerCase());
};

/**
 * Check if file extension is a document
 * @param {string} ext - File extension
 * @returns {boolean}
 */
export const isDocumentExtension = ext => {
  return ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx', 'txt', 'md', 'rtf'].includes(
    ext?.toLowerCase()
  );
};

export default {
  getFileIcon,
  getFileTypeTooltip,
  formatFileSize,
  isImageExtension,
  isDocumentExtension,
};
